(() => {
  class TishaCalculator {
    constructor(root) {
      this.root = root;
      if (this.root.dataset.calcReady === 'true') return;
      this.root.dataset.calcReady = 'true';

      this.config = this.loadConfig();
      if (!this.config || this.config.settings?.enabled === false) {
        this.root.setAttribute('hidden', '');
        return;
      }

      this.state = {};
      this.outputs = {};
      this.controls = {};
      this.userOverrides = new Set();
      this.lastResults = null;
      this.locale = document.documentElement.lang || 'en-GB';
      this.currency = this.config.shop?.currency || 'GBP';
      this.currencyFormatter = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: 0,
      });
      this.currencyFormatterPrecise = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      this.integerFormatter = new Intl.NumberFormat(this.locale, {
        maximumFractionDigits: 0,
      });
      this.sectionId = this.config.section_id || this.root.dataset.sectionId || null;
      this.analytics = {
        started: false,
        completed: false,
        viewTracked: false,
        resultViewed: false,
        lastStep: '',
        actualSpendError: false,
      };
      this.startTime = null;
      this.leadContextActive = false;
      this.advancedUpdated = false;
      this.stepState = {
        current: 1,
        total: 4,
      };
      this.utmParams = this.getUtmParams();

      const discountConfig = this.config.discounts || {};
      const defaultBulkMessage = 'Bulk discount -{percent}% ({min_qty}+ cases)';
      this.bulkDiscountConfig = this.parseBulkConfigValue(discountConfig.bulk_tiers) || {};
      this.bulkMessageTemplate = this.bulkDiscountConfig.message_template || defaultBulkMessage;
      this.bulkTiers = this.normalizeBulkTiers(this.bulkDiscountConfig);
      if (!this.bulkTiers.length) {
        this.bulkTiers = this.normalizeBulkTiers({
          tiers: [
            { min_qty: 5, percent: 5 },
            { min_qty: 10, percent: 8 },
            { min_qty: 25, percent: 12 },
          ],
        });
      }
      this.subscriptionPercent = this.toNumber(discountConfig.subscription_percent, 0);

      this.products = (this.config.products || [])
        .filter((product) => {
          if (!product || !product.variant_id || !product.units_per_case) return false;
          if (product.is_active === false || product.enabled === false) return false;
          return true;
        })
        .map((product) => {
          const category = this.normalizeCategory(product.category);
          return {
            ...product,
            category,
            quality_tier: product.quality_tier || product.tier || 'standard',
            is_case_product: this.normalizeBoolean(product.is_case_product),
            bulk_discount_eligible: this.normalizeBoolean(product.bulk_discount_eligible),
            subscription_eligible: this.normalizeBoolean(product.subscription_eligible),
            selling_plan_id: this.toNumber(product.selling_plan_id, 0) || null,
          };
        })
        .filter((product) => product.category)
        .sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999));
      this.productsByKey = Object.fromEntries(this.products.map((product) => [product.key, product]));
      this.productsByCategory = this.products.reduce((acc, product) => {
        if (!acc[product.category]) acc[product.category] = [];
        acc[product.category].push(product);
        return acc;
      }, {});

      this.rules = (this.config.rules || []).filter((rule) => {
        if (!rule) return false;
        if (rule.enabled === false || rule.is_active === false) return false;
        return true;
      });
      this.profiles = (this.config.profiles || []).filter((profile) => profile && profile.key);
      this.locationTypeDefinitions = this.getLocationTypeDefinitions();
      this.competitorPricing = (this.config.competitor_pricing || [])
        .filter((price) => {
          if (!price) return false;
          if (price.enabled === false || price.is_active === false) return false;
          return true;
        })
        .map((price) => ({
          ...price,
          category: this.normalizeCategory(price.category),
          quality_tier: price.quality_tier || price.tier || 'standard',
        }))
        .filter((price) => price.category);

      this.bindOutputs();
      this.bindControls();
      this.initializeState();
      this.populateSelects();
      this.initializeWizard();
      this.bindEvents();
      this.bindAnchors();
      this.update(true);
      this.bindAnalytics();
      this.track('calc_open');
    }

    loadConfig() {
      const configEl = this.root.querySelector('script[type="application/json"]');
      if (!configEl) return null;
      try {
        return JSON.parse(configEl.textContent);
      } catch (error) {
        console.warn('TISHA calculator config error', error);
        return null;
      }
    }

    bindOutputs() {
      this.root.querySelectorAll('[data-calc-output]').forEach((node) => {
        this.outputs[node.dataset.calcOutput] = node;
      });
      this.errorEl = this.root.querySelector('[data-calc-error]');
      this.debugEl = this.root.querySelector('[data-calc-debug]');
      this.advancedBadgeEl = this.root.querySelector('[data-calc-advanced-badge]');
      this.profileHintEl = this.outputs.profile_hint || null;
    }

    bindControls() {
      const selectors = [
        'visits_per_day',
        'visits_per_day_number',
        'open_days_select',
        'open_days_custom',
        'location_type',
        'tp_enabled',
        'towels_enabled',
        'seat_enabled',
        'hand_drying_mode',
        'profile',
        'towel_share',
        'tp_preset',
        'tp_sheets_min',
        'tp_sheets_max',
        'towel_sheets_min',
        'towel_sheets_max',
        'seat_share',
        'quality_tier',
        'mode',
        'actual_spend',
        'purchase_mode',
      ];
      selectors.forEach((selector) => {
        const nodes = this.root.querySelectorAll(`[data-calc-input="${selector}"]`);
        if (!nodes.length) return;
        this.controls[selector] = nodes.length === 1 ? nodes[0] : Array.from(nodes);
      });

      this.controls.tp_product = this.root.querySelector('[data-calc-select="tp_product"]');
      this.controls.towel_product = this.root.querySelector('[data-calc-select="towel_product"]');
      this.controls.seat_product = this.root.querySelector('[data-calc-select="seat_product"]');
      this.controls.calculate = this.root.querySelector('[data-action="calculate"]');
      this.controls.updateResults = this.root.querySelector('[data-action="update-results"]');
      this.controls.reset = this.root.querySelector('[data-action="reset"]');
      this.controls.orderKit = this.root.querySelector('[data-action="order-kit"]');
      this.controls.getQuote = this.root.querySelector('[data-action="get-quote"]');
      this.controls.quoteModal = this.root.querySelector('[data-calc-quote-modal]');
      this.controls.quoteClose = Array.from(this.root.querySelectorAll('[data-action="close-quote"]'));
      this.controls.stepPanels = Array.from(this.root.querySelectorAll('[data-calc-step-panel]'));
      this.controls.stepItems = Array.from(this.root.querySelectorAll('[data-calc-step-item]'));
      this.controls.stepNext = Array.from(this.root.querySelectorAll('[data-action="step-next"]'));
      this.controls.stepBack = Array.from(this.root.querySelectorAll('[data-action="step-back"]'));
      this.controls.stepResults = Array.from(this.root.querySelectorAll('[data-action="step-results"]'));
      this.controls.stepEdit = Array.from(this.root.querySelectorAll('[data-action="step-edit"]'));
      this.controls.profileField = this.root.querySelector('[data-calc-profile-field]');
      this.controls.resultsSavings = this.root.querySelector('[data-calc-results-savings]');
      this.controls.resultsMode = this.root.querySelector('[data-calc-results-mode]');
      this.controls.resultsCta = this.root.querySelector('[data-calc-results-cta]');
      this.controls.resultsCtaNote = this.root.querySelector('[data-calc-results-cta-note]');
      this.controls.resultsBreakdown = this.root.querySelector('[data-calc-results-breakdown]');
      this.controls.resultsAdvanced = this.root.querySelector('[data-calc-results-advanced]');
    }

    initializeState() {
      const defaults = {
        visitsPerDay: 140,
        openDays: 30,
        tpUsersShare: 1,
        towelUsersShare: 0.75,
        seatCoverUsersShare: 0.2,
        tpSheetsMin: 8,
        tpSheetsMax: 12,
        towelSheetsMin: 2,
        towelSheetsMax: 3,
        seatCoverShare: 0.2,
      };

      const ruleInfo = this.resolveContextDefaults();
      this.ruleInfo = ruleInfo;
      this.locationProfileMap = this.createLocationProfileMap();
      this.syncLocationOptions();

      const profile = this.getProfileByKey(ruleInfo.defaultProfileKey) || null;
      const profileDefaults = this.profileToDefaults(profile);
      const base = { ...defaults, ...profileDefaults };
      const contextSetup = this.resolveContextSetup(ruleInfo);
      const locationType = this.getLocationTypeForProfile(profile?.key || '');

      this.state = {
        visitsPerDay: base.visitsPerDay,
        openDays: base.openDays,
        tpUsersShare: base.tpUsersShare,
        towelUsersShare: base.towelUsersShare,
        seatCoverUsersShare: base.seatCoverUsersShare,
        tpSheetsMin: base.tpSheetsMin,
        tpSheetsMax: base.tpSheetsMax,
        towelSheetsMin: base.towelSheetsMin,
        towelSheetsMax: base.towelSheetsMax,
        tpEnabled: contextSetup.tpEnabled,
        towelsEnabled: contextSetup.towelsEnabled,
        seatEnabled: contextSetup.seatEnabled,
        handDryingMode: contextSetup.handDryingMode,
        tpProductKey: ruleInfo.defaultProducts.toilet_paper,
        towelProductKey: ruleInfo.defaultProducts.towels,
        seatProductKey: ruleInfo.defaultProducts.seat_covers,
        profileKey: profile ? profile.key : '',
        locationType,
        tpPreset: this.resolveTpPreset(base.tpSheetsMin, base.tpSheetsMax),
        qualityTier: 'standard',
        mode: ruleInfo.mode,
        actualSpend: '',
        purchaseMode: 'one_time',
      };
      this.lastTowelProductKey = this.state.towelProductKey;
      this.lastTowelsEnabled = this.state.towelsEnabled;
      this.lastTpProductKey = this.state.tpProductKey;
      this.lastSeatProductKey = this.state.seatProductKey;

      if (this.config.settings?.show_profiles === false && this.controls.profileField) {
        this.controls.profileField.classList.add('is-hidden');
      }

      this.applyStateToInputs();
      this.setAdvancedUpdated(false);
    }

    getLocationTypeDefinitions() {
      return [
        { key: 'office', keywords: ['office', 'cowork', 'co-work', 'workspace'] },
        { key: 'restaurant', keywords: ['restaurant', 'cafe', 'bar', 'pub', 'horeca'] },
        { key: 'cafe', keywords: ['cafe', 'coffee', 'bistro'] },
        { key: 'healthcare', keywords: ['health', 'clinic', 'medical', 'care', 'hospital', 'dental'] },
        { key: 'gym', keywords: ['gym', 'fitness', 'sport', 'studio', 'leisure'] },
        { key: 'hotel', keywords: ['hotel', 'hospitality', 'guest', 'lobby'] },
        { key: 'school', keywords: ['school', 'education', 'college', 'university', 'campus'] },
        { key: 'other', keywords: [] },
      ];
    }

    createLocationProfileMap() {
      const map = {};
      const used = new Set();
      this.locationTypeDefinitions.forEach((definition) => {
        if (definition.key === 'other') return;
        const match = this.profiles.find((profile) => {
          if (used.has(profile.key)) return false;
          const haystack = `${profile.key} ${profile.title_public || ''}`.toLowerCase();
          return definition.keywords.some((keyword) => haystack.includes(keyword));
        });
        if (!match) return;
        map[definition.key] = match.key;
        used.add(match.key);
      });

      const fallback = this.profiles.find((profile) => !used.has(profile.key));
      if (fallback) {
        map.other = fallback.key;
      }
      if (!map.cafe && map.restaurant) {
        map.cafe = map.restaurant;
      }
      return map;
    }

    syncLocationOptions() {
      if (!Array.isArray(this.controls.location_type)) return;
      this.controls.location_type.forEach((radio) => {
        const label = radio.closest('.tisha-calculator__chip');
        const mapped = this.locationProfileMap?.[radio.value];
        const disabled = radio.value !== 'other' && !mapped;
        radio.disabled = disabled;
        label?.classList.toggle('is-disabled', disabled);
      });
    }

    getLocationTypeForProfile(profileKey) {
      if (!profileKey) return 'other';
      const match = Object.entries(this.locationProfileMap || {}).find(([, value]) => value === profileKey);
      return match ? match[0] : 'other';
    }

    getProfileForLocationType(locationType) {
      if (!locationType) return '';
      if (locationType === 'cafe' && this.locationProfileMap?.restaurant) {
        return this.locationProfileMap.restaurant;
      }
      return this.locationProfileMap?.[locationType] || '';
    }

    getLocationTypeLabel(locationType) {
      const key = String(locationType || '').trim();
      if (!key) return '';
      if (key === 'cafe') return 'Cafe';
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }

    getProfileContextLabel() {
      const profile = this.getProfileByKey(this.state.profileKey);
      const profileLabel = profile?.title_public || '';
      const locationLabel = this.getLocationTypeLabel(this.state.locationType);

      if (this.state.locationType === 'cafe') {
        return 'Cafe';
      }

      return profileLabel || locationLabel;
    }

    resolveContextSetup(ruleInfo) {
      const contextCategory = this.normalizeCategory(ruleInfo?.contextCategory || this.config.context?.product_category);
      const setup = {
        tpEnabled: true,
        towelsEnabled: true,
        seatEnabled: Boolean(ruleInfo?.coversOn),
        handDryingMode: 'paper_towels',
      };

      if (contextCategory === 'seat_covers') {
        setup.seatEnabled = true;
      } else if (contextCategory === 'towels') {
        setup.towelsEnabled = true;
        setup.handDryingMode = 'paper_towels';
      } else if (contextCategory === 'toilet_paper') {
        setup.tpEnabled = true;
      }

      return setup;
    }

    setAdvancedUpdated(isUpdated = true) {
      this.advancedUpdated = Boolean(isUpdated);
      if (!this.advancedBadgeEl) return;
      this.advancedBadgeEl.classList.toggle('is-hidden', !this.advancedUpdated);
    }

    initializeWizard() {
      this.setStep(1, { track: false, scrollResults: false, render: false });
    }

    setStep(step, options = {}) {
      const nextStep = Math.min(this.stepState.total, Math.max(1, Number(step) || 1));
      const prevStep = this.stepState.current;
      this.stepState.current = nextStep;
      this.root.dataset.calcStep = String(nextStep);

      if (Array.isArray(this.controls.stepPanels)) {
        this.controls.stepPanels.forEach((panel) => {
          const isActive = Number(panel.dataset.calcStepPanel) === nextStep;
          panel.classList.toggle('is-hidden', !isActive);
        });
      }

      if (Array.isArray(this.controls.stepItems)) {
        this.controls.stepItems.forEach((item) => {
          const target = Number(item.dataset.stepTarget);
          item.classList.toggle('is-active', target === nextStep);
          item.classList.toggle('is-complete', target < nextStep);
          if (target === nextStep) {
            item.setAttribute('aria-current', 'step');
          } else {
            item.removeAttribute('aria-current');
          }
        });
      }

      this.refreshProfileHint();

      if (nextStep === 4 && options.scrollResults !== false) {
        const resultsPanel = this.root.querySelector('.tisha-calculator__panel--results');
        const mobile = window.matchMedia('(max-width: 990px)').matches;
        if (mobile && resultsPanel) {
          resultsPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }

      if (options.track !== false && prevStep !== nextStep) {
        if (nextStep === 1) this.trackStep('location');
        if (nextStep === 2) this.trackStep('traffic');
        if (nextStep === 3) this.trackStep('products');
        if (nextStep === 4) this.trackStep('results');
      }

      if (options.render !== false) {
        this.update(true);
      }
    }

    refreshProfileHint() {
      if (!this.profileHintEl) return;
      const profileLabel = this.getProfileContextLabel();
      if (profileLabel) {
        this.profileHintEl.textContent = `Profile: ${profileLabel}`;
        this.profileHintEl.classList.remove('is-hidden');
      } else {
        this.profileHintEl.classList.add('is-hidden');
      }
    }

    hasTrafficInput() {
      return this.userOverrides.has('visits_per_day') || this.userOverrides.has('open_days');
    }

    toggleResultsStage(step, options = {}) {
      const showFull = Boolean(options.showFull);
      const showSavings = Boolean(options.showSavings);
      const previewSavings = Boolean(options.previewSavings);

      if (this.controls.resultsSavings) {
        this.controls.resultsSavings.classList.toggle('is-hidden', !showSavings);
        this.controls.resultsSavings.classList.toggle('is-preview', previewSavings);
      }
      if (this.controls.resultsMode) {
        this.controls.resultsMode.classList.toggle('is-hidden', !showFull);
      }
      if (this.controls.resultsCta) {
        this.controls.resultsCta.classList.toggle('is-hidden', !showFull);
      }
      if (this.controls.resultsCtaNote) {
        this.controls.resultsCtaNote.classList.toggle('is-hidden', !showFull);
      }
      if (this.controls.resultsBreakdown) {
        this.controls.resultsBreakdown.classList.toggle('is-hidden', !showFull);
      }
      if (this.controls.resultsAdvanced) {
        this.controls.resultsAdvanced.classList.toggle('is-hidden', !showFull);
      }

      if (this.outputs.savings_phase) {
        if (previewSavings) {
          this.outputs.savings_phase.textContent = 'Preview (typical assumptions)';
          this.outputs.savings_phase.classList.remove('is-hidden');
        } else {
          this.outputs.savings_phase.classList.add('is-hidden');
        }
      }

      if (this.outputs.phase_note) {
        let phaseNote = '';
        if (step === 1) {
          const profileLabel = this.getProfileContextLabel();
          phaseNote = 'Estimates will appear after you set traffic and current setup.';
          if (profileLabel) {
            phaseNote = `Using typical assumptions for ${profileLabel}. Estimates will appear after you set traffic and current setup.`;
          }
        } else if (step === 2) {
          phaseNote = 'Complete setup to unlock full comparison.';
        } else if (step === 3) {
          phaseNote = 'Preview values use typical assumptions. Final controls are on Results.';
        }
        this.outputs.phase_note.textContent = phaseNote;
        this.outputs.phase_note.classList.toggle('is-hidden', !phaseNote);
      }
    }

    resolveContextDefaults() {
      const context = this.config.context || {};
      const settings = this.config.settings || {};
      const embed = this.config.embed || {};
      const baseDefaults = this.getBestSavingsDefaults();

      const defaults = {
        defaultProducts: { ...baseDefaults.defaultProducts },
        defaultProfileKey: settings.default_profile_key || baseDefaults.defaultProfileKey,
        coversOn: baseDefaults.coversOn,
        mode: this.normalizeMode(settings.default_mode) || baseDefaults.mode,
        contextCategory: '',
        contextProductKey: '',
        appliedRules: [],
      };

      if (settings.global_rule_key) {
        const globalRule = this.rules.find((rule) => rule.key === settings.global_rule_key);
        if (globalRule) {
          this.applyRuleToDefaults(defaults, globalRule, { lock: false });
          defaults.appliedRules.push(globalRule.key || '');
        }
      }

      const matchingRules = this.findMatchingRules(context);
      const locks = {
        profile: false,
        tp: false,
        towel: false,
        cover: false,
        coversOn: false,
      };

      for (const rule of matchingRules) {
        this.applyRuleToDefaults(defaults, rule, { lock: true, locks });
        defaults.appliedRules.push(rule.key || '');
        if (rule.stop_processing) break;
      }

      this.applyProductOverride(defaults, context);
      this.applyEmbedOverrides(defaults, embed);

      defaults.defaultProducts.toilet_paper = this.resolveProductKey(
        defaults.defaultProducts.toilet_paper,
        'toilet_paper'
      );
      defaults.defaultProducts.towels = this.resolveProductKey(defaults.defaultProducts.towels, 'towels');
      defaults.defaultProducts.seat_covers = this.resolveProductKey(
        defaults.defaultProducts.seat_covers,
        'seat_covers',
        true
      );

      if (defaults.coversOn && !defaults.defaultProducts.seat_covers) {
        defaults.defaultProducts.seat_covers = this.resolveProductKey('', 'seat_covers');
      }

      return defaults;
    }

    getBestSavingsDefaults() {
      return {
        defaultProducts: {
          toilet_paper: this.resolveProductKey('B106', 'toilet_paper'),
          towels: this.resolveProductKey('P130', 'towels'),
          seat_covers: '',
        },
        defaultProfileKey: 'best_savings',
        coversOn: false,
        mode: 'market_average',
      };
    }

    findMatchingRules(context) {
      const rules = [...this.rules].sort((a, b) => (b.priority || 0) - (a.priority || 0));
      return rules.filter((rule) => this.ruleMatches(rule, context));
    }

    ruleMatches(rule, context) {
      const type = rule.match_type || rule.apply_to || '';
      const value = rule.match_value || rule.apply_value;
      const hasValue = value !== null && value !== undefined && value !== '';

      if (type === 'global') return true;
      if (!hasValue) return false;

      if (type === 'template') return String(context.template) === String(value);
      if (type === 'collection_handle' || type === 'collection')
        return String(context.collection_handle) === String(value);
      if (type === 'product_tag') return Array.isArray(context.product_tags) && context.product_tags.includes(value);
      if (type === 'product_type') return String(context.product_type) === String(value);
      if (type === 'url_contains') {
        const url = context.url || window.location.pathname;
        return typeof url === 'string' && url.includes(value);
      }
      return false;
    }

    applyRuleToDefaults(defaults, rule, options = {}) {
      const lock = options.lock;
      const locks = options.locks || {};

      const applyIfUnlocked = (key, value, lockKey) => {
        if (!value) return;
        if (lock && locks[lockKey]) return;
        defaults[key] = value;
        if (lock) locks[lockKey] = true;
      };

      applyIfUnlocked('defaultProfileKey', rule.apply_preset_key || rule.default_profile_key, 'profile');

      if (rule.force_tp_key || rule.default_tp_key) {
        if (!lock || !locks.tp) {
          defaults.defaultProducts.toilet_paper = rule.force_tp_key || rule.default_tp_key;
          if (lock) locks.tp = true;
        }
      }

      if (rule.force_towel_key || rule.default_towel_key) {
        if (!lock || !locks.towel) {
          defaults.defaultProducts.towels = rule.force_towel_key || rule.default_towel_key;
          if (lock) locks.towel = true;
        }
      }

      if (rule.force_cover_key || rule.default_seat_key) {
        if (!lock || !locks.cover) {
          defaults.defaultProducts.seat_covers = rule.force_cover_key || rule.default_seat_key;
          defaults.coversOn = true;
          if (lock) locks.cover = true;
        }
      }

      if (rule.force_covers_on === true) {
        if (!lock || !locks.coversOn) {
          defaults.coversOn = true;
          if (lock) locks.coversOn = true;
        }
      }
    }

    applyProductOverride(defaults, context) {
      if (context.page_type !== 'product') return;
      let category = this.normalizeCategory(context.product_category);
      if (!category && context.product_variant_id) {
        const variantMatch = this.findProductByVariantId(context.product_variant_id);
        if (variantMatch?.category) {
          category = variantMatch.category;
        }
      }
      if (!category) return;
      defaults.contextCategory = category;

      const refKey = context.product_calc_product_key;
      let productKey = '';
      if (refKey && this.productsByKey[refKey]) {
        productKey = refKey;
      } else if (context.product_variant_id) {
        const match = this.findProductByVariantId(context.product_variant_id);
        if (match && match.category === category) {
          productKey = match.key;
        }
      }

      if (category === 'seat_covers') {
        defaults.coversOn = true;
        if (!productKey) {
          defaults.defaultProducts.seat_covers = this.resolveProductKey('TC250', 'seat_covers');
          return;
        }
      }

      if (!productKey) return;
      defaults.defaultProducts[category] = productKey;
      defaults.contextProductKey = productKey;

      if (category === 'seat_covers') {
        defaults.coversOn = true;
      }
    }

    applyEmbedOverrides(defaults, embed) {
      if (!embed) return;
      if (embed.preset) {
        defaults.defaultProfileKey = embed.preset;
      }

      const embedMode = this.normalizeMode(embed.mode);
      if (embedMode) {
        defaults.mode = embedMode;
      }

      if (embed.force_tp) {
        defaults.defaultProducts.toilet_paper = embed.force_tp;
      }
      if (embed.force_towels) {
        defaults.defaultProducts.towels = embed.force_towels;
      }
      if (embed.force_covers) {
        defaults.defaultProducts.seat_covers = embed.force_covers;
        defaults.coversOn = true;
      }

      if (embed.covers_on === true || embed.covers_on === false) {
        defaults.coversOn = embed.covers_on;
      }
    }

    normalizeCategory(category) {
      if (!category) return null;
      const normalized = String(category).toLowerCase();
      if (normalized === 'tp' || normalized === 'toilet_paper' || normalized === 'toilet-paper') return 'toilet_paper';
      if (normalized === 'towel' || normalized === 'towels' || normalized === 'paper_towels') return 'towels';
      if (normalized === 'seat_covers' || normalized === 'seat_cover' || normalized === 'seat-cover' || normalized === 'seat-covers')
        return 'seat_covers';
      if (normalized === 'other' || normalized === 'none') return null;
      return null;
    }

    normalizeMode(mode) {
      if (!mode) return '';
      if (mode === 'market_avg') return 'market_average';
      if (mode === 'market_average') return 'market_average';
      if (mode === 'actual_spend') return 'actual_spend';
      return mode;
    }

    resolveProductKey(key, category, allowEmpty = false) {
      if (!key && allowEmpty) return '';
      if (key && this.productsByKey[key]) return key;
      return this.firstProductKey(category);
    }

    findProductByVariantId(variantId) {
      const id = Number(variantId);
      return this.products.find((product) => Number(product.variant_id) === id) || null;
    }

    firstProductKey(category) {
      return this.productsByCategory[category]?.[0]?.key || '';
    }

    getProfileByKey(key) {
      return this.profiles.find((profile) => profile.key === key) || null;
    }

    profileToDefaults(profile) {
      if (!profile) return {};
      return {
        visitsPerDay: this.toNumber(profile.default_visits_per_day, 140),
        openDays: this.toNumber(profile.default_open_days_per_month, 30),
        tpUsersShare: this.normalizeShare(profile.tp_users_share, 1),
        towelUsersShare: this.normalizeShare(profile.towel_users_share, 0.75),
        seatCoverUsersShare: this.normalizeShare(
          profile.seat_cover_users_share ?? profile.seat_cover_share,
          0.2
        ),
        tpSheetsMin: this.toNumber(profile.tp_sheets_per_visit_min, 8),
        tpSheetsMax: this.toNumber(profile.tp_sheets_per_visit_max, 12),
        towelSheetsMin: this.toNumber(profile.towel_sheets_per_use_min, 2),
        towelSheetsMax: this.toNumber(profile.towel_sheets_per_use_max, 3),
      };
    }

    populateSelects() {
      this.populateSelect(
        this.controls.tp_product,
        this.productsByCategory.toilet_paper || [],
        this.state.tpProductKey
      );
      this.populateSelect(
        this.controls.towel_product,
        this.productsByCategory.towels || [],
        this.state.towelProductKey
      );
      this.populateSelect(
        this.controls.seat_product,
        this.productsByCategory.seat_covers || [],
        this.state.seatProductKey
      );

      if (this.controls.profile) {
        this.controls.profile.innerHTML = '<option value="">Use typical defaults</option>';
        this.profiles.forEach((profile) => {
          const option = document.createElement('option');
          option.value = profile.key;
          option.textContent = profile.title_public || profile.key;
          if (profile.key === this.state.profileKey) option.selected = true;
          this.controls.profile.appendChild(option);
        });
      }
      this.syncLocationOptions();
    }

    populateSelect(select, products, selectedKey) {
      if (!select) return;
      const placeholder = select.querySelector('option[value=""]')?.textContent;
      select.innerHTML = '';
      if (!products.length) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = 'No products available';
        select.appendChild(option);
        select.disabled = true;
        return;
      }
      select.disabled = false;
      if (placeholder) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = placeholder;
        select.appendChild(option);
      }
      let hasSelected = false;
      products.forEach((product) => {
        const option = document.createElement('option');
        option.value = product.key;
        option.textContent = product.title_public || product.key;
        if (product.key === selectedKey) {
          option.selected = true;
          hasSelected = true;
        }
        select.appendChild(option);
      });
      if (hasSelected) {
        select.value = selectedKey;
      } else if (!selectedKey && placeholder) {
        select.value = '';
      } else {
        select.value = products[0]?.key || '';
      }
    }

    applyStateToInputs() {
      this.setInputValue(this.controls.visits_per_day, this.state.visitsPerDay);
      this.setInputValue(this.controls.visits_per_day_number, this.state.visitsPerDay);
      if (this.controls.open_days_select) {
        const optionValues = Array.from(this.controls.open_days_select.options).map((option) => option.value);
        if (optionValues.includes(String(this.state.openDays))) {
          this.setInputValue(this.controls.open_days_select, String(this.state.openDays));
        } else {
          this.setInputValue(this.controls.open_days_select, 'custom');
        }
      }
      this.setInputValue(this.controls.open_days_custom, this.state.openDays);
      this.setInputChecked(this.controls.tp_enabled, this.state.tpEnabled);
      this.setInputChecked(this.controls.towels_enabled, this.state.towelsEnabled);
      this.setInputChecked(this.controls.seat_enabled, this.state.seatEnabled);
      this.setRadioValue(this.controls.location_type, this.state.locationType);
      this.setInputValue(this.controls.tp_product, this.state.tpProductKey);
      this.setInputValue(this.controls.towel_product, this.state.towelProductKey);
      this.setInputValue(this.controls.seat_product, this.state.seatProductKey);
      this.setRadioValue(this.controls.hand_drying_mode, this.state.handDryingMode);
      this.setRadioValue(this.controls.mode, this.state.mode);
      this.setRadioValue(this.controls.purchase_mode, this.state.purchaseMode);
      this.setInputValue(this.controls.actual_spend, this.state.actualSpend);
      this.setInputValue(this.controls.towel_share, Math.round(this.state.towelUsersShare * 100));
      this.setInputValue(this.controls.seat_share, Math.round(this.state.seatCoverUsersShare * 100));
      this.setInputValue(this.controls.tp_preset, this.state.tpPreset);
      this.setInputValue(this.controls.tp_sheets_min, this.state.tpSheetsMin);
      this.setInputValue(this.controls.tp_sheets_max, this.state.tpSheetsMax);
      this.setInputValue(this.controls.towel_sheets_min, this.state.towelSheetsMin);
      this.setInputValue(this.controls.towel_sheets_max, this.state.towelSheetsMax);
      this.setInputValue(this.controls.quality_tier, this.state.qualityTier);

      this.toggleCustomOpenDays();
      this.toggleActualSpend();
      this.refreshProfileHint();
    }

    bindEvents() {
      if (Array.isArray(this.controls.stepItems)) {
        this.controls.stepItems.forEach((item) => {
          item.addEventListener('click', () => {
            const target = Number(item.dataset.stepTarget);
            this.trackCalcStart();
            this.setStep(target);
          });
        });
      }

      if (Array.isArray(this.controls.stepNext)) {
        this.controls.stepNext.forEach((button) => {
          button.addEventListener('click', () => {
            this.trackCalcStart();
            this.setStep(this.stepState.current + 1);
          });
        });
      }

      if (Array.isArray(this.controls.stepBack)) {
        this.controls.stepBack.forEach((button) => {
          button.addEventListener('click', () => {
            this.trackCalcStart();
            this.setStep(this.stepState.current - 1, { scrollResults: false });
          });
        });
      }

      if (Array.isArray(this.controls.stepResults)) {
        this.controls.stepResults.forEach((button) => {
          button.addEventListener('click', () => {
            this.handleInteraction('results');
            this.setStep(4);
          });
        });
      }

      if (Array.isArray(this.controls.stepEdit)) {
        this.controls.stepEdit.forEach((button) => {
          button.addEventListener('click', () => {
            const target = Number(button.dataset.stepTarget);
            if (!target) return;
            this.trackCalcStart();
            this.setStep(target, { scrollResults: false });
          });
        });
      }

      if (Array.isArray(this.controls.location_type)) {
        this.controls.location_type.forEach((radio) => {
          radio.addEventListener('change', (event) => {
            if (!event.target.checked) return;
            this.handleInteraction('location');
            this.markUserOverride('location_type');
            this.handleLocationTypeChange(event.target.value);
          });
        });
      }

      if (this.controls.visits_per_day) {
        this.controls.visits_per_day.addEventListener('input', (event) => {
          this.handleInteraction('traffic');
          this.markUserOverride('visits_per_day');
          const value = this.toNumber(event.target.value, this.state.visitsPerDay);
          this.updateState({ visitsPerDay: value, visitsChange: true });
          this.setInputValue(this.controls.visits_per_day_number, value);
        });
      }

      if (this.controls.visits_per_day_number) {
        this.controls.visits_per_day_number.addEventListener('input', (event) => {
          this.handleInteraction('traffic');
          this.markUserOverride('visits_per_day');
          const value = this.toNumber(event.target.value, this.state.visitsPerDay);
          this.updateState({ visitsPerDay: value, visitsChange: true });
          this.setInputValue(this.controls.visits_per_day, value);
        });
      }

      if (this.controls.open_days_select) {
        this.controls.open_days_select.addEventListener('change', (event) => {
          this.handleInteraction('traffic');
          this.markUserOverride('open_days');
          const value = event.target.value;
          if (value === 'custom') {
            this.toggleCustomOpenDays(true);
          } else {
            const numeric = this.toNumber(value, this.state.openDays);
            this.updateState({ openDays: numeric, visitsChange: true });
            this.toggleCustomOpenDays(false);
          }
        });
      }

      if (this.controls.open_days_custom) {
        this.controls.open_days_custom.addEventListener('input', (event) => {
          this.handleInteraction('traffic');
          this.markUserOverride('open_days');
          const value = this.toNumber(event.target.value, this.state.openDays);
          this.updateState({ openDays: value, visitsChange: true });
        });
      }

      if (this.controls.tp_enabled) {
        this.controls.tp_enabled.addEventListener('change', (event) => {
          this.handleInteraction('products');
          this.markUserOverride('tp_enabled');
          const enabled = event.target.checked;
          const nextState = { tpEnabled: enabled, toggleCategory: true };
          if (!enabled) {
            if (this.state.tpProductKey) {
              this.lastTpProductKey = this.state.tpProductKey;
            }
            nextState.tpProductKey = '';
          } else if (!this.state.tpProductKey) {
            const restoreKey =
              this.lastTpProductKey ||
              this.ruleInfo?.defaultProducts?.toilet_paper ||
              this.firstProductKey('toilet_paper');
            if (restoreKey) {
              nextState.tpProductKey = restoreKey;
            }
          }
          this.updateState(nextState);
        });
      }

      if (this.controls.towels_enabled) {
        this.controls.towels_enabled.addEventListener('change', (event) => {
          this.handleInteraction('products');
          this.markUserOverride('towels_enabled');
          const enabled = event.target.checked;
          const nextState = { towelsEnabled: enabled, toggleCategory: true };
          if (this.state.handDryingMode === 'paper_towels') {
            this.lastTowelsEnabled = enabled;
          }
          if (!enabled) {
            if (this.state.towelProductKey) {
              this.lastTowelProductKey = this.state.towelProductKey;
            }
            nextState.towelProductKey = '';
          } else if (!this.state.towelProductKey && this.state.handDryingMode === 'paper_towels') {
            const restoreKey =
              this.lastTowelProductKey ||
              this.ruleInfo?.defaultProducts?.towels ||
              this.firstProductKey('towels');
            if (restoreKey) {
              nextState.towelProductKey = restoreKey;
            }
          }
          this.updateState(nextState);
        });
      }

      if (this.controls.seat_enabled) {
        this.controls.seat_enabled.addEventListener('change', (event) => {
          this.handleInteraction('products');
          this.markUserOverride('seat_enabled');
          const enabled = event.target.checked;
          const nextState = { seatEnabled: enabled, toggleCategory: true };
          if (!enabled) {
            if (this.state.seatProductKey) {
              this.lastSeatProductKey = this.state.seatProductKey;
            }
            nextState.seatProductKey = '';
          } else if (!this.state.seatProductKey) {
            const restoreKey =
              this.lastSeatProductKey ||
              this.ruleInfo?.defaultProducts?.seat_covers ||
              this.firstProductKey('seat_covers');
            if (restoreKey) {
              nextState.seatProductKey = restoreKey;
            }
          }
          this.updateState(nextState);
        });
      }

      if (this.controls.hand_drying_mode) {
        this.controls.hand_drying_mode.forEach((radio) => {
          radio.addEventListener('change', (event) => {
            this.handleInteraction('products');
            this.markUserOverride('hand_drying_mode');
            this.handleHandDryingModeChange(event.target.value);
          });
        });
      }

      if (this.controls.tp_product) {
        this.controls.tp_product.addEventListener('change', (event) => {
          this.handleInteraction('products');
          this.markUserOverride('tp_product');
          const value = event.target.value;
          if (this.state.tpEnabled) {
            this.lastTpProductKey = value;
          }
          this.updateState({ tpProductKey: value, selectProduct: true });
        });
      }

      if (this.controls.towel_product) {
        this.controls.towel_product.addEventListener('change', (event) => {
          this.handleInteraction('products');
          this.markUserOverride('towel_product');
          const value = event.target.value;
          if (this.state.handDryingMode === 'paper_towels') {
            this.lastTowelProductKey = value;
          }
          this.updateState({ towelProductKey: value, selectProduct: true });
        });
      }

      if (this.controls.seat_product) {
        this.controls.seat_product.addEventListener('change', (event) => {
          this.handleInteraction('products');
          this.markUserOverride('seat_product');
          const value = event.target.value;
          if (this.state.seatEnabled) {
            this.lastSeatProductKey = value;
          }
          this.updateState({ seatProductKey: value, selectProduct: true });
        });
      }

      if (this.controls.profile) {
        this.controls.profile.addEventListener('change', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('profile');
          this.setAdvancedUpdated(true);
          const profile = this.getProfileByKey(event.target.value);
          const locationType = this.getLocationTypeForProfile(profile ? profile.key : '');
          this.applyProfileDefaults(profile, { locationType, force: true });
        });
      }

      if (this.controls.towel_share) {
        this.controls.towel_share.addEventListener('input', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('towel_share');
          this.setAdvancedUpdated(true);
          const value = this.toNumber(event.target.value, 0) / 100;
          this.updateState({ towelUsersShare: value });
        });
      }

      if (this.controls.seat_share) {
        this.controls.seat_share.addEventListener('input', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('seat_share');
          this.setAdvancedUpdated(true);
          const value = this.toNumber(event.target.value, 0) / 100;
          this.updateState({ seatCoverUsersShare: value });
        });
      }

      if (this.controls.tp_preset) {
        this.controls.tp_preset.addEventListener('change', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('tp_preset');
          this.setAdvancedUpdated(true);
          const preset = event.target.value;
          const presetValues = this.tpPresetValues(preset);
          this.updateState(
            {
              tpPreset: preset,
              tpSheetsMin: presetValues.min,
              tpSheetsMax: presetValues.max,
            },
            true
          );
        });
      }

      if (this.controls.tp_sheets_min) {
        this.controls.tp_sheets_min.addEventListener('input', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('tp_sheets_min');
          this.setAdvancedUpdated(true);
          this.updateState({ tpSheetsMin: this.toNumber(event.target.value, this.state.tpSheetsMin), tpPreset: 'custom' });
          this.setInputValue(this.controls.tp_preset, 'custom');
        });
      }

      if (this.controls.tp_sheets_max) {
        this.controls.tp_sheets_max.addEventListener('input', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('tp_sheets_max');
          this.setAdvancedUpdated(true);
          this.updateState({ tpSheetsMax: this.toNumber(event.target.value, this.state.tpSheetsMax), tpPreset: 'custom' });
          this.setInputValue(this.controls.tp_preset, 'custom');
        });
      }

      if (this.controls.towel_sheets_min) {
        this.controls.towel_sheets_min.addEventListener('input', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('towel_sheets_min');
          this.setAdvancedUpdated(true);
          this.updateState({ towelSheetsMin: this.toNumber(event.target.value, this.state.towelSheetsMin) });
        });
      }

      if (this.controls.towel_sheets_max) {
        this.controls.towel_sheets_max.addEventListener('input', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('towel_sheets_max');
          this.setAdvancedUpdated(true);
          this.updateState({ towelSheetsMax: this.toNumber(event.target.value, this.state.towelSheetsMax) });
        });
      }

      if (this.controls.quality_tier) {
        this.controls.quality_tier.addEventListener('change', (event) => {
          this.handleInteraction('advanced');
          this.markUserOverride('quality_tier');
          this.setAdvancedUpdated(true);
          this.updateState({ qualityTier: event.target.value, toggleMode: true });
        });
      }

      if (this.controls.mode) {
        this.controls.mode.forEach((radio) => {
          radio.addEventListener('change', (event) => {
            this.handleInteraction('results');
            this.markUserOverride('mode');
            this.updateState({ mode: event.target.value, toggleMode: true });
            this.toggleActualSpend();
          });
        });
      }

      if (this.controls.purchase_mode) {
        this.controls.purchase_mode.forEach((radio) => {
          radio.addEventListener('change', (event) => {
            this.handleInteraction('purchase_option');
            this.markUserOverride('purchase_mode');
            const nextMode = event.target.value;
            const prevMode = this.state.purchaseMode;
            this.updateState({ purchaseMode: nextMode, toggleDiscount: true });
            if (nextMode === 'subscribe' && prevMode !== 'subscribe') {
              this.track('select_refill_plan', this.lastResults, {
                interval: 'unspecified',
                plan_type: 'subscribe',
              });
            }
          });
        });
      }

      if (this.controls.actual_spend) {
        this.controls.actual_spend.addEventListener('input', (event) => {
          this.handleInteraction('results');
          this.markUserOverride('actual_spend');
          this.updateState({ actualSpend: event.target.value });
        });
      }

      if (this.controls.calculate) {
        this.controls.calculate.addEventListener('click', () => {
          this.handleInteraction('results');
          this.update(false, true);
        });
      }

      if (this.controls.updateResults) {
        this.controls.updateResults.addEventListener('click', () => {
          this.handleInteraction('results');
          this.update(false, true);
        });
      }

      if (this.controls.reset) {
        this.controls.reset.addEventListener('click', () => {
          const profile = this.getProfileByKey(this.state.profileKey);
          const defaults = this.profileToDefaults(profile);
          const preset = this.resolveTpPreset(
            defaults.tpSheetsMin ?? this.state.tpSheetsMin,
            defaults.tpSheetsMax ?? this.state.tpSheetsMax
          );
          this.updateState(
            {
              visitsPerDay: defaults.visitsPerDay ?? 140,
              openDays: defaults.openDays ?? 30,
              tpUsersShare: defaults.tpUsersShare ?? 1,
              towelUsersShare: defaults.towelUsersShare ?? 0.75,
              seatCoverUsersShare: defaults.seatCoverUsersShare ?? 0.2,
              tpSheetsMin: defaults.tpSheetsMin ?? 8,
              tpSheetsMax: defaults.tpSheetsMax ?? 12,
              towelSheetsMin: defaults.towelSheetsMin ?? 2,
              towelSheetsMax: defaults.towelSheetsMax ?? 3,
              tpPreset: preset,
            },
            true
          );
          this.setAdvancedUpdated(false);
        });
      }

      if (this.controls.orderKit) {
        this.controls.orderKit.addEventListener('click', () => {
          this.handleInteraction('results');
          this.addKitToCart();
        });
      }

      if (this.controls.getQuote) {
        this.controls.getQuote.addEventListener('click', () => {
          this.handleInteraction('results');
          this.trackLeadOpen();
          this.trackCalcComplete(this.lastResults);
          this.track('calc_get_quote', this.lastResults);
          const hasB2bPopup =
            this.controls.getQuote.hasAttribute('data-b2b-popup-open') &&
            document.getElementById('b2b-popup');
          if (!hasB2bPopup) {
            this.openQuoteModal();
          }
        });
      }

      if (this.controls.quoteClose.length) {
        this.controls.quoteClose.forEach((btn) => {
          btn.addEventListener('click', () => this.closeQuoteModal());
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!this.controls.quoteModal || this.controls.quoteModal.classList.contains('is-hidden')) return;
        this.closeQuoteModal();
      });

      /* If form posted_successfully — auto-open modal in success state + 12s auto-close */
      this.initSuccessAutoClose();
    }

    markUserOverride(key) {
      if (!key) return;
      this.userOverrides.add(key);
    }

    applyProfileDefaults(profile, options = {}) {
      const defaults = this.profileToDefaults(profile);
      const nextLocationType =
        options.locationType || this.getLocationTypeForProfile(profile ? profile.key : '');
      this.updateState(
        {
          profileKey: profile ? profile.key : '',
          locationType: nextLocationType,
          visitsPerDay: defaults.visitsPerDay ?? this.state.visitsPerDay,
          openDays: defaults.openDays ?? this.state.openDays,
          tpUsersShare: defaults.tpUsersShare ?? this.state.tpUsersShare,
          towelUsersShare: defaults.towelUsersShare ?? this.state.towelUsersShare,
          seatCoverUsersShare: defaults.seatCoverUsersShare ?? this.state.seatCoverUsersShare,
          tpSheetsMin: defaults.tpSheetsMin ?? this.state.tpSheetsMin,
          tpSheetsMax: defaults.tpSheetsMax ?? this.state.tpSheetsMax,
          towelSheetsMin: defaults.towelSheetsMin ?? this.state.towelSheetsMin,
          towelSheetsMax: defaults.towelSheetsMax ?? this.state.towelSheetsMax,
          tpPreset: this.resolveTpPreset(
            defaults.tpSheetsMin ?? this.state.tpSheetsMin,
            defaults.tpSheetsMax ?? this.state.tpSheetsMax
          ),
        },
        options.force === true
      );
    }

    handleLocationTypeChange(locationType) {
      const mappedProfileKey = this.getProfileForLocationType(locationType);
      const mappedProfile = mappedProfileKey ? this.getProfileByKey(mappedProfileKey) : null;

      if (mappedProfile) {
        this.applyProfileDefaults(mappedProfile, {
          locationType,
          force: true,
        });
        this.setAdvancedUpdated(false);
        return;
      }

      this.updateState(
        {
          locationType,
          profileKey: '',
        },
        true
      );
      this.setAdvancedUpdated(false);
    }

    openQuoteModal() {
      if (!this.controls.quoteModal) return;
      this.controls.quoteModal.classList.remove('is-hidden');
      this.controls.quoteModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
    }

    closeQuoteModal() {
      if (!this.controls.quoteModal) return;
      this.controls.quoteModal.classList.add('is-hidden');
      this.controls.quoteModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      /* Strip ?contact_posted=true so reload doesn't re-open success */
      try {
        if (window.location.search.indexOf('contact_posted') !== -1) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      } catch (e) {}
    }

    /* Auto-open modal in success state + run 12s countdown + auto-close */
    initSuccessAutoClose() {
      if (!this.controls.quoteModal) return;
      const success = this.controls.quoteModal.querySelector('[data-calc-quote-success]');
      if (!success) return;
      this.openQuoteModal();
      const countdown = success.querySelector('[data-calc-quote-countdown]');
      let seconds = 12;
      const timer = setInterval(() => {
        seconds -= 1;
        if (countdown) countdown.textContent = seconds;
        if (seconds <= 0) {
          clearInterval(timer);
          this.closeQuoteModal();
        }
      }, 1000);
    }

    bindAnchors() {
      const anchors = document.querySelectorAll('[data-calc-anchor]');
      if (!anchors.length) return;
      const defaultStep = this.config.context?.page_type === 'product' ? 2 : 1;

      const isVisible =
        !this.root.hasAttribute('hidden') &&
        (this.root.offsetParent || this.root.getClientRects().length);

      if (!isVisible) return;

      anchors.forEach((anchor) => {
        if (anchor.dataset.calcAnchorBound === 'true') return;
        anchor.dataset.calcAnchorBound = 'true';
        anchor.removeAttribute('hidden');
        anchor.addEventListener('click', (event) => {
          event.preventDefault();
          const targetStep = Number(anchor.dataset.calcAnchorStep) || defaultStep;
          this.trackCalcStart();
          this.setStep(targetStep);
          this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });

      document.querySelectorAll('[data-calc-anchor-note]').forEach((note) => {
        note.removeAttribute('hidden');
      });
    }

    bindAnalytics() {
      this.bindViewObserver();
      this.bindResultObserver();
      this.bindLeadForms();
    }

    bindViewObserver() {
      if (this.analytics.viewTracked) return;
      if (!('IntersectionObserver' in window)) {
        this.trackViewCalculator();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || this.analytics.viewTracked) return;
            this.trackViewCalculator();
            observer.disconnect();
          });
        },
        { threshold: 0.35 }
      );

      observer.observe(this.root);
    }

    bindResultObserver() {
      if (this.analytics.resultViewed) return;
      const panel = this.root.querySelector('.tisha-calculator__panel--results');
      if (!panel) return;

      if (!('IntersectionObserver' in window)) {
        this.trackViewResult();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || this.analytics.resultViewed) return;
            this.trackViewResult();
            observer.disconnect();
          });
        },
        { threshold: 0.35 }
      );

      observer.observe(panel);
    }

    bindLeadForms() {
      const quoteForm = this.root.querySelector('[data-calc-quote-form]');
      if (quoteForm && quoteForm.dataset.calcLeadBound !== 'true') {
        quoteForm.dataset.calcLeadBound = 'true';
        quoteForm.addEventListener('submit', () => {
          this.track('leadform_submit', this.lastResults, {
            form_type: 'quote',
            segment_hint: this.getSegmentHint(),
          });
          // generate_lead уніфікований event для GA4 конверсій (Smart Bidding)
          if (window.tishaTrackLead) {
            var kitValueCents = this.getKitValue(this.lastResults);
            window.tishaTrackLead({
              form_type: 'b2b_quote',
              value: kitValueCents > 0 ? Math.round(kitValueCents) / 100 : undefined,
            });
          }
          this.leadContextActive = false;
        });
      }

      const popupForm = document.getElementById('b2b-popup-contact');
      if (popupForm && popupForm.dataset.calcLeadBound !== 'true') {
        popupForm.dataset.calcLeadBound = 'true';
        popupForm.addEventListener('submit', () => {
          if (!this.leadContextActive) return;
          this.track('leadform_submit', this.lastResults, {
            form_type: 'quote',
            segment_hint: this.getSegmentHint(),
          });
          // generate_lead уніфікований event для GA4 конверсій (Smart Bidding)
          if (window.tishaTrackLead) {
            var kitValueCents = this.getKitValue(this.lastResults);
            window.tishaTrackLead({
              form_type: 'b2b_quote',
              value: kitValueCents > 0 ? Math.round(kitValueCents) / 100 : undefined,
            });
          }
          this.leadContextActive = false;
        });
      }
    }

    handleInteraction(stepName) {
      this.trackCalcStart();
      this.trackStep(stepName);
    }

    trackCalcStart() {
      if (this.analytics.started) return;
      this.analytics.started = true;
      this.startTime = Date.now();
      this.track('calc_start', this.lastResults, {
        segment_hint: this.getSegmentHint(),
        device: this.getDeviceType(),
      });
    }

    trackStep(stepName) {
      if (!stepName) return;
      const stepNumber = this.getStepNumber(stepName);
      if (!stepNumber) return;
      if (this.analytics.lastStep === stepName) return;
      this.analytics.lastStep = stepName;
      this.track('calc_step', this.lastResults, {
        step_name: stepName,
        step_number: stepNumber,
      });
    }

    getStepNumber(stepName) {
      const steps = {
        location: 1,
        traffic: 2,
        products: 3,
        purchase_option: 3,
        advanced: 4,
        results: 4,
      };
      return steps[stepName] || null;
    }

    trackViewCalculator() {
      if (this.analytics.viewTracked) return;
      this.analytics.viewTracked = true;
      const { source, medium, campaign } = this.utmParams || {};
      this.track('view_calculator', this.lastResults, {
        source: source || null,
        medium: medium || null,
        campaign: campaign || null,
        landing_page: this.getLandingPage(),
      });
    }

    trackViewResult() {
      if (this.analytics.resultViewed) return;
      this.analytics.resultViewed = true;
      this.track('view_result', this.lastResults, {
        recommended_kit_id: this.getKitId(this.lastResults),
        savings_value: this.getSavingsValue(this.lastResults),
      });
    }

    trackLeadOpen() {
      this.leadContextActive = true;
      this.track('lead_open', this.lastResults, {
        form_type: 'quote',
        segment_hint: this.getSegmentHint(),
      });
      window.clearTimeout(this.leadContextTimer);
      this.leadContextTimer = window.setTimeout(() => {
        this.leadContextActive = false;
      }, 5 * 60 * 1000);
    }

    trackCalcComplete(results) {
      if (this.analytics.completed || !this.analytics.started) return;
      if (!this.isResultValid(results)) return;

      const items = this.buildKitLineItems(results);
      const kitValueCents = this.getKitValue(results, items);
      const completionTime = this.startTime ? Date.now() - this.startTime : null;

      this.analytics.completed = true;
      this.track('calc_complete', results, {
        total_value: this.toCurrencyValue(kitValueCents),
        currency: this.currency,
        items_count: items.length,
        discount_type: this.getDiscountType(items),
        time_to_complete_ms: completionTime,
      });
    }

    trackValidationErrors(invalidActual) {
      if (invalidActual && !this.analytics.actualSpendError) {
        this.analytics.actualSpendError = true;
        this.track('calc_error', this.lastResults, {
          step_name: 'results',
          field_name: 'actual_spend',
        });
      }
      if (!invalidActual && this.analytics.actualSpendError) {
        this.analytics.actualSpendError = false;
      }
    }

    isResultValid(results) {
      if (!results || !results.totals) return false;
      if (results.totals.configInvalid) return false;
      if (results.totals.actualInvalid) return false;
      return true;
    }

    getSegmentHint() {
      return (
        this.state.locationType ||
        this.state.profileKey ||
        this.ruleInfo?.defaultProfileKey ||
        this.config.context?.product_category ||
        this.config.context?.product_type ||
        null
      );
    }

    getDeviceType() {
      const width = window.innerWidth || document.documentElement.clientWidth || 0;
      if (width <= 640) return 'mobile';
      if (width <= 1024) return 'tablet';
      return 'desktop';
    }

    getUtmParams() {
      if (typeof window === 'undefined') return {};
      const params = new URLSearchParams(window.location.search || '');
      return {
        source: params.get('utm_source'),
        medium: params.get('utm_medium'),
        campaign: params.get('utm_campaign'),
      };
    }

    getLandingPage() {
      if (typeof window === 'undefined') return null;
      return `${window.location.pathname}${window.location.search || ''}`;
    }

    buildKitLineItems(results = this.lastResults) {
      if (!results || !results.categories) return [];
      const items = [];

      const addCategory = (category, categoryKey) => {
        if (!category?.enabled || !category.product) return;
        const quantity = this.getCartQuantity(category);
        if (quantity < 1) return;
        const discountPercent = this.getDiscountPercentForQuantity(category, quantity);
        const discountType = discountPercent > 0 ? (this.state.purchaseMode === 'subscribe' ? 'subscription' : 'bulk') : 'none';
        const basePrice = this.getTishaPrice(category.product);
        const price = this.applyDiscount(basePrice, discountPercent);

        items.push({
          category: categoryKey,
          product: category.product,
          quantity,
          price,
          discountPercent,
          discountType,
        });
      };

      addCategory(results.categories.toilet_paper, 'toilet_paper');
      addCategory(results.categories.towels, 'towels');
      addCategory(results.categories.seat_covers, 'seat_covers');

      return items;
    }

    getKitId(results = this.lastResults) {
      const items = this.buildKitLineItems(results);
      const keys = items
        .map((item) => item.product?.key || item.product?.variant_id)
        .filter(Boolean);
      if (!keys.length) return null;
      return `kit:${keys.join('|')}`;
    }

    getKitValue(results = this.lastResults, items = null) {
      const lineItems = items || this.buildKitLineItems(results);
      if (!lineItems.length) return 0;
      return lineItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    }

    getSavingsValue(results = this.lastResults) {
      const savings = results?.totals?.savingMax;
      if (!Number.isFinite(savings)) return null;
      return this.toCurrencyValue(savings);
    }

    getDiscountType(items) {
      let hasSubscription = false;
      let hasBulk = false;
      items.forEach((item) => {
        if (item.discountType === 'subscription') hasSubscription = true;
        if (item.discountType === 'bulk') hasBulk = true;
      });
      if (hasSubscription) return 'subscription';
      if (hasBulk) return 'bulk';
      return 'none';
    }

    getDiscountPercentForQuantity(category, quantity) {
      const product = category?.product;
      if (!product || quantity < 1) return 0;
      if (this.state.purchaseMode === 'subscribe' && product.subscription_eligible && product.selling_plan_id) {
        return this.subscriptionPercent;
      }
      if (this.isBulkEligible(product)) {
        const tier = this.getBulkTier(quantity);
        return tier ? tier.percent : 0;
      }
      return 0;
    }

    toCurrencyValue(cents) {
      if (!Number.isFinite(cents)) return null;
      return Number((cents / 100).toFixed(2));
    }

    buildAddToCartPayload(results = this.lastResults) {
      const items = this.buildKitLineItems(results);
      if (!items.length) return null;

      let totalCents = 0;
      const skuList = [];
      const quantityTotal = items.reduce((sum, item) => {
        totalCents += item.price * item.quantity;
        const id = item.product?.variant_id;
        if (id) skuList.push(id);
        return sum + item.quantity;
      }, 0);
      const kitValue = this.toCurrencyValue(totalCents);

      return {
        currency: this.currency,
        value: kitValue,
        price: kitValue,
        kit_id: this.getKitId(results),
        kit_value: kitValue,
        kit_items_count: items.length,
        sku: skuList.join('|') || null,
        qty: quantityTotal,
        discount_applied: this.getDiscountType(items),
      };
    }

    handleHandDryingModeChange(mode) {
      const nextState = {
        handDryingMode: mode,
        toggleCategory: true,
        handDryingModeChange: true,
      };

      if (mode === 'air_dryer') {
        if (this.state.towelProductKey) {
          this.lastTowelProductKey = this.state.towelProductKey;
        }
        if (typeof this.state.towelsEnabled === 'boolean') {
          this.lastTowelsEnabled = this.state.towelsEnabled;
        }
        nextState.towelsEnabled = false;
        nextState.towelProductKey = '';
      } else {
        const restoreEnabled =
          typeof this.lastTowelsEnabled === 'boolean' ? this.lastTowelsEnabled : true;
        nextState.towelsEnabled = restoreEnabled;
        const restoreKey =
          this.lastTowelProductKey ||
          this.ruleInfo?.defaultProducts?.towels ||
          this.firstProductKey('towels');
        if (restoreKey) {
          nextState.towelProductKey = restoreKey;
        }
      }

      this.updateState(nextState);
    }


    updateState(nextState, force = false) {
      this.state = { ...this.state, ...nextState };

      if (this.state.handDryingMode === 'paper_towels' && this.state.towelProductKey) {
        this.lastTowelProductKey = this.state.towelProductKey;
      }
      if (this.state.handDryingMode === 'paper_towels' && typeof this.state.towelsEnabled === 'boolean') {
        this.lastTowelsEnabled = this.state.towelsEnabled;
      }
      if (this.state.tpEnabled && this.state.tpProductKey) {
        this.lastTpProductKey = this.state.tpProductKey;
      }
      if (this.state.seatEnabled && this.state.seatProductKey) {
        this.lastSeatProductKey = this.state.seatProductKey;
      }

      this.applyStateToInputs();
      this.update(force);

      if (nextState.visitsChange) this.track('calc_change_traffic', this.lastResults);
      if (nextState.toggleCategory) this.track('calc_toggle_category', this.lastResults);
      if (nextState.selectProduct) this.track('calc_select_product', this.lastResults);
      if (nextState.toggleMode) this.track('calc_toggle_mode', this.lastResults);
      if (nextState.handDryingModeChange) {
        this.track('calc_hand_drying_mode_change', this.lastResults, {
          mode: this.state.handDryingMode,
        });
      }
    }

    update(force = false, fromCalculate = false) {
      if (!this.products.length) {
        this.showError('No calculator products are active.');
        return;
      }

      this.root.classList.toggle('is-calculating', true);
      const results = this.calculate();
      this.lastResults = results;
      this.render(results);
      this.trackCalcComplete(results);
      if (fromCalculate) {
        this.track('calc_calculate', results);
      }

      if (force) {
        this.root.classList.remove('is-calculating');
      } else {
        window.setTimeout(() => {
          this.root.classList.remove('is-calculating');
        }, 250);
      }
    }

    calculate() {
      const visitsPerMonth = Math.max(0, this.state.visitsPerDay * this.state.openDays);
      const tpEnabled = this.state.tpEnabled;
      const towelsEnabled = this.state.towelsEnabled && this.state.handDryingMode === 'paper_towels';
      const seatEnabled = this.state.seatEnabled;
      const categories = {};

      const tpResults = this.calculateCategory({
        id: 'toilet_paper',
        enabled: tpEnabled,
        eventsShare: this.state.tpUsersShare,
        unitsMin: this.state.tpSheetsMin,
        unitsMax: this.state.tpSheetsMax,
        productKey: this.state.tpProductKey,
      });
      const towelResults = this.calculateCategory({
        id: 'towels',
        enabled: towelsEnabled,
        eventsShare: this.state.towelUsersShare,
        unitsMin: this.state.towelSheetsMin,
        unitsMax: this.state.towelSheetsMax,
        productKey: this.state.towelProductKey,
      });
      const seatResults = this.calculateCategory({
        id: 'seat_covers',
        enabled: seatEnabled,
        eventsShare: this.state.seatCoverUsersShare,
        unitsMin: 1,
        unitsMax: 1,
        productKey: this.state.seatProductKey,
      });

      categories.toilet_paper = tpResults;
      categories.towels = towelResults;
      categories.seat_covers = seatResults;

      const totals = this.sumTotals(categories, visitsPerMonth);
      totals.configInvalid = Object.values(categories).some((category) => category.enabled && !category.product);

      return {
        visitsPerMonth,
        categories,
        totals,
        mode: this.state.mode,
      };
    }

    calculateCategory({ id, enabled, eventsShare, unitsMin, unitsMax, productKey }) {
      const product = this.productsByKey[productKey] || null;
      const unitsPerCase = product ? this.toNumber(product.units_per_case, 0) : 0;
      const wasteFactor = product ? this.toNumber(product.waste_factor, 1) : 1;
      const events = enabled ? this.state.visitsPerDay * this.state.openDays * eventsShare : 0;

      const minUnits = enabled ? Math.max(0, events * Math.min(unitsMin, unitsMax) * wasteFactor) : 0;
      const maxUnits = enabled ? Math.max(0, events * Math.max(unitsMin, unitsMax) * wasteFactor) : 0;

      const minCases = enabled && unitsPerCase ? Math.ceil(minUnits / unitsPerCase) : 0;
      const maxCases = enabled && unitsPerCase ? Math.ceil(maxUnits / unitsPerCase) : 0;

      const tishaPricePerCase = this.getTishaPrice(product);
      const tishaMinBase = minCases * tishaPricePerCase;
      const tishaMaxBase = maxCases * tishaPricePerCase;
      const discount = this.getDiscountInfo(product, minCases, maxCases);
      const tishaMin = this.applyDiscount(tishaMinBase, discount.percentMin);
      const tishaMax = this.applyDiscount(tishaMaxBase, discount.percentMax);
      const discountMin = Math.max(0, tishaMinBase - tishaMin);
      const discountMax = Math.max(0, tishaMaxBase - tishaMax);

      const competitor = this.getCompetitorPrice(id);
      const currentMin = minCases * competitor.min;
      const currentMax = maxCases * competitor.max;

      return {
        id,
        enabled,
        product,
        unitsMin,
        unitsMax,
        minUnits,
        maxUnits,
        minCases,
        maxCases,
        tishaMinBase,
        tishaMaxBase,
        tishaMin,
        tishaMax,
        discountMin,
        discountMax,
        currentMin,
        currentMax,
        discount,
      };
    }

    sumTotals(categories, visitsPerMonth) {
      let tishaMin = 0;
      let tishaMax = 0;
      let currentMin = 0;
      let currentMax = 0;
      let discountMin = 0;
      let discountMax = 0;

      Object.values(categories).forEach((category) => {
        if (!category.enabled) return;
        tishaMin += category.tishaMin;
        tishaMax += category.tishaMax;
        currentMin += category.currentMin;
        currentMax += category.currentMax;
        discountMin += category.discountMin || 0;
        discountMax += category.discountMax || 0;
      });

      const totals = {
        tishaMin,
        tishaMax,
        currentMin,
        currentMax,
        discountMin,
        discountMax,
      };

      if (this.state.mode === 'actual_spend') {
        const actual = this.toNumber(this.state.actualSpend, NaN);
        if (!actual || Number.isNaN(actual)) {
          totals.actualInvalid = true;
        } else {
          const cents = Math.round(actual * 100);
          totals.currentMin = cents;
          totals.currentMax = cents;
        }
      }

      const savingRange = this.calculateSavingsRange(totals);
      totals.savingMin = savingRange.min;
      totals.savingMax = savingRange.max;
      totals.savingPercentMin = savingRange.percentMin;
      totals.savingPercentMax = savingRange.percentMax;
      totals.tishaPerVisitMin = visitsPerMonth ? tishaMin / visitsPerMonth : 0;
      totals.tishaPerVisitMax = visitsPerMonth ? tishaMax / visitsPerMonth : 0;
      totals.currentPerVisitMin = visitsPerMonth ? totals.currentMin / visitsPerMonth : 0;
      totals.currentPerVisitMax = visitsPerMonth ? totals.currentMax / visitsPerMonth : 0;

      return totals;
    }

    calculateSavingsRange(totals) {
      const currentMin = totals.currentMin || 0;
      const currentMax = totals.currentMax || 0;
      const tishaMin = totals.tishaMin || 0;
      const tishaMax = totals.tishaMax || 0;

      const savingMin = currentMin - tishaMax;
      const savingMax = currentMax - tishaMin;

      let percentMin = null;
      let percentMax = null;
      if (currentMin > 0) {
        percentMin = savingMin / currentMin;
      }
      if (currentMax > 0) {
        percentMax = savingMax / currentMax;
      }

      return {
        min: savingMin,
        max: savingMax,
        percentMin,
        percentMax,
      };
    }

    render(results) {
      const step = Number(this.stepState.current) || 1;
      const showTraffic = step >= 2;
      const showComparison = step >= 4;
      const showSavings = step >= 4;
      const showFull = step >= 4;
      const previewSavings = false;

      this.toggleResultsStage(step, {
        showFull,
        showSavings,
        previewSavings,
      });

      const visitsPerMonth = results.visitsPerMonth;
      const visitsPerMonthLabel = `${this.integerFormatter.format(Math.round(visitsPerMonth))} visits/month`;
      this.setOutput(
        'visits_month',
        showTraffic ? `${this.integerFormatter.format(Math.round(visitsPerMonth))} visits per month` : '—'
      );
      this.setOutput(
        'visits_per_month_note',
        showTraffic ? `${this.state.openDays} days open per month` : 'Set traffic to calculate'
      );

      const totals = results.totals;
      const invalidActual = totals.actualInvalid && this.state.mode === 'actual_spend';
      const effectiveInvalidActual = showFull ? invalidActual : false;
      const tishaBaseRange = this.formatMoneyRange(totals.tishaMin, totals.tishaMax);
      const tishaRange = tishaBaseRange === '-' ? '-' : `${tishaBaseRange} per month`;
      const currentBaseRange = this.formatMoneyRange(totals.currentMin, totals.currentMax);
      const currentRange = effectiveInvalidActual
        ? '-'
        : currentBaseRange === '-'
          ? '-'
          : `${currentBaseRange} per month`;

      if (showComparison) {
        this.setOutput('tisha_cost_range', tishaRange);
        this.setOutput('current_cost_range', currentRange);
      } else if (step === 2) {
        this.setOutput('tisha_cost_range', 'Complete setup to compare');
        this.setOutput('current_cost_range', 'Complete setup to compare');
      } else {
        this.setOutput('tisha_cost_range', 'Locked');
        this.setOutput('current_cost_range', 'Locked');
      }
      this.trackValidationErrors(effectiveInvalidActual);

      const discountInfo = showFull && showComparison ? this.buildDiscountInfo(results) : { tishaNote: '', extraNote: '' };
      if (this.outputs.tisha_discount_note) {
        this.outputs.tisha_discount_note.textContent = discountInfo.tishaNote;
        this.outputs.tisha_discount_note.classList.toggle('is-hidden', !discountInfo.tishaNote);
      }
      if (this.outputs.discount_note) {
        this.outputs.discount_note.textContent = discountInfo.extraNote;
        this.outputs.discount_note.classList.toggle('is-hidden', !discountInfo.extraNote);
      }

      const savings = !effectiveInvalidActual && showSavings ? this.calculateSavingsRange(totals) : null;
      let savingTitle = 'Save -';
      let savingMonthRange = '-';
      let savingYearRange = '-';
      let savingHeroTitle = 'Save - / year';
      let savingHeroSub = '~ - vs typical supplier';
      let savingNote = '';
      let savingMultiplier = '';

      if (savings) {
        const savingMin = savings.min;
        const savingMax = savings.max;
        const pctMin = savings.percentMin;
        const pctMax = savings.percentMax;

        if (savingMax <= 0) {
          savingTitle = 'Costs more';
          const costMoreMin = Math.abs(savingMax);
          const costMoreMax = Math.abs(savingMin);
          savingMonthRange = this.formatMoneyRange(costMoreMin, costMoreMax);
          savingYearRange = this.formatMoneyRange(costMoreMin * 12, costMoreMax * 12);
          savingHeroTitle = `Costs more: ${savingYearRange} / year`;
          savingHeroSub = 'vs typical supplier';
        } else if (savingMin <= 0) {
          const upToMonth = this.currencyFormatter.format(savingMax / 100);
          const upToYear = this.currencyFormatter.format((savingMax * 12) / 100);
          const pctUpTo = Number.isFinite(pctMax) ? `${Math.max(0, Math.round(pctMax * 100))}%` : '-';
          savingTitle = pctUpTo === '-' ? 'Save up to' : `Save up to ${pctUpTo}`;
          savingMonthRange = `Up to ${upToMonth}`;
          savingYearRange = `Up to ${upToYear}`;
          savingHeroTitle = `Save up to ${upToYear} / year`;
          savingHeroSub = pctUpTo === '-' ? 'vs typical supplier' : `≈ up to ${pctUpTo} vs typical supplier`;
          savingNote = 'In some cases costs may be similar or higher (depends on usage/pricing).';
        } else {
          const pctRange = this.formatPercentRange(pctMin, pctMax);
          savingTitle = pctRange === '-' ? 'Save' : `Save ${pctRange}`;
          savingMonthRange = this.formatMoneyRange(savingMin, savingMax);
          savingYearRange = this.formatMoneyRange(savingMin * 12, savingMax * 12);
          savingHeroTitle = `Save ${savingYearRange} / year`;
          savingHeroSub = pctRange === '-' ? 'vs typical supplier' : `≈ ${pctRange} vs typical supplier`;
        }

        const cheaperMax = totals.tishaMin > 0 ? totals.currentMax / totals.tishaMin : 0;
        if (cheaperMax >= 1.05) {
          savingMultiplier = `Up to ${cheaperMax.toFixed(1)}x cheaper`;
        }
      } else if (step === 1) {
        savingHeroTitle = 'Locked';
        savingHeroSub = 'Set traffic and setup to unlock savings.';
      } else if (step === 2) {
        savingHeroTitle = 'Locked';
        savingHeroSub = 'Complete setup to preview savings.';
      } else if (step === 3) {
        savingHeroTitle = 'Preview unavailable';
        savingHeroSub = 'Set traffic to preview savings.';
      }

      this.setOutput('saving_title', savingTitle);
      this.setOutput('saving_month_range', savingMonthRange);
      this.setOutput('saving_year_range', savingYearRange);
      this.setOutput('saving_hero_title', savingHeroTitle);
      this.setOutput('saving_hero_sub', savingHeroSub);
      if (this.outputs.saving_note) {
        this.outputs.saving_note.textContent = savingNote;
        this.outputs.saving_note.classList.toggle('is-hidden', !savingNote || !showSavings);
      }
      if (this.outputs.saving_multiplier) {
        this.outputs.saving_multiplier.textContent = savingMultiplier;
        this.outputs.saving_multiplier.classList.toggle('is-hidden', !savingMultiplier || !showSavings);
      }

      const tishaPerVisit = showSavings ? this.formatPenceRange(totals.tishaPerVisitMin, totals.tishaPerVisitMax) : '-';
      const currentPerVisit =
        showSavings && !effectiveInvalidActual
          ? this.formatPenceRange(totals.currentPerVisitMin, totals.currentPerVisitMax)
          : '-';
      this.setOutput('tisha_cost_per_visit', tishaPerVisit);
      this.setOutput('current_cost_per_visit', currentPerVisit);
      this.setOutput(
        'cost_per_visit_line',
        showSavings ? `${tishaPerVisit} with TISHA • ${currentPerVisit} currently` : '-'
      );

      this.setOutput('towel_share_value', `${Math.round(this.state.towelUsersShare * 100)}%`);
      this.setOutput('seat_share_value', `${Math.round(this.state.seatCoverUsersShare * 100)}%`);

      if (showFull) {
        this.renderCategory(results.categories.toilet_paper, {
          titleKey: 'tp_product_title',
          unitsKey: 'tp_units_range',
          casesKey: 'tp_cases_range',
          tishaKey: 'tp_tisha_cost',
          currentKey: 'tp_current_cost',
          unitLabel: 'sheets',
          selector: 'toilet_paper',
          showWhenDisabled: true,
          disabledLabel: 'Not included',
        });

        this.renderCategory(results.categories.towels, {
          titleKey: 'towel_product_title',
          unitsKey: 'towel_units_range',
          casesKey: 'towel_cases_range',
          tishaKey: 'towel_tisha_cost',
          currentKey: 'towel_current_cost',
          unitLabel: 'sheets',
          selector: 'towels',
          showWhenDisabled: true,
          disabledLabel: 'Not included',
        });

        this.renderCategory(results.categories.seat_covers, {
          titleKey: 'seat_product_title',
          unitsKey: 'seat_units_range',
          casesKey: 'seat_cases_range',
          tishaKey: 'seat_tisha_cost',
          currentKey: 'seat_current_cost',
          unitLabel: 'covers',
          selector: 'seat_covers',
          showWhenDisabled: true,
          disabledLabel: 'Not included',
        });
      }

      if (totals.configInvalid) {
        this.showError('Missing calculator product data. Please check the calculator configuration.');
      } else {
        this.hideError();
      }
      this.updateActualSpendValidation(effectiveInvalidActual);

      if (this.debugEl && this.config.settings?.debug_mode) {
        this.debugEl.hidden = false;
        this.debugEl.textContent = JSON.stringify(results, null, 2);
      } else if (this.debugEl) {
        this.debugEl.hidden = true;
      }

      this.setOutput(
        'mode_note',
        this.state.mode === 'actual_spend'
          ? 'Using your actual spend to compare costs.'
          : 'Based on average UK wholesale pricing for similar quality.'
      );
      const typeSummary = this.getProfileContextLabel() || '-';
      this.setOutput('step_profile_summary', `Profile: ${typeSummary}`);
      this.setOutput('step_traffic_summary', `Traffic: ${visitsPerMonthLabel}`);
      const includedSummary = [];
      if (this.state.tpEnabled) includedSummary.push('TP');
      if (this.state.handDryingMode === 'air_dryer') {
        includedSummary.push('Air dryer');
      } else if (this.state.towelsEnabled) {
        includedSummary.push('Towels');
      }
      if (this.state.seatEnabled) includedSummary.push('Seat covers');
      if (!includedSummary.length) includedSummary.push('None');
      const purchaseSummary = this.state.purchaseMode === 'subscribe' ? 'Subscription' : 'One-time';
      this.setOutput(
        'step_setup_summary',
        `Included: ${includedSummary.join(' + ')} • Purchase: ${purchaseSummary}`
      );
      this.setOutput('results_type', typeSummary);
      this.setOutput('results_traffic', visitsPerMonthLabel);
      const setupSummary = [];
      if (this.state.tpEnabled) setupSummary.push('Toilet paper');
      if (this.state.handDryingMode === 'air_dryer') {
        setupSummary.push('Air dryer');
      } else if (this.state.towelsEnabled) {
        setupSummary.push('Paper towels');
      }
      if (this.state.seatEnabled) setupSummary.push('Seat covers');
      if (!setupSummary.length) setupSummary.push('No categories selected');
      setupSummary.push(this.state.purchaseMode === 'subscribe' ? 'Subscribe & Save' : 'One-time purchase');
      this.setOutput('results_setup', setupSummary.join(' • '));
      this.toggleActualSpend();
      this.toggleTpControls();
      this.toggleTowelControls();
      this.toggleSeatControls();
    }

    renderCategory(result, config) {
      const row = this.root.querySelector(`[data-calc-breakdown="${config.selector}"]`);
      if (!row) return;

      const isAirDryer = config.selector === 'towels' && this.state.handDryingMode === 'air_dryer';
      if (isAirDryer) {
        row.removeAttribute('hidden');
        this.setOutput(config.titleKey, 'Not included (Air dryer)');
        this.setOutput(config.unitsKey, '-');
        this.setOutput(config.casesKey, '-');
        this.setOutput(config.tishaKey, '-');
        this.setOutput(config.currentKey, '-');
        return;
      }

      if (!result.enabled) {
        if (config.showWhenDisabled) {
          row.removeAttribute('hidden');
          this.setOutput(config.titleKey, config.disabledLabel || 'Not included');
          this.setOutput(config.unitsKey, '-');
          this.setOutput(config.casesKey, '-');
          this.setOutput(config.tishaKey, '-');
          this.setOutput(config.currentKey, '-');
          return;
        }
        row.setAttribute('hidden', '');
        return;
      }

      if (!result.product) {
        row.setAttribute('hidden', '');
        return;
      }
      row.removeAttribute('hidden');

      const title = result.product?.title_public || result.product?.variant_title || '-';
      this.setOutput(config.titleKey, title);
      this.setOutput(config.unitsKey, this.formatNumberRange(result.minUnits, result.maxUnits));
      this.setOutput(config.casesKey, this.formatNumberRange(result.minCases, result.maxCases));
      this.setOutput(config.tishaKey, this.formatMoneyRange(result.tishaMin, result.tishaMax));

      if (this.state.mode === 'actual_spend') {
        this.setOutput(config.currentKey, '-');
      } else {
        this.setOutput(config.currentKey, this.formatMoneyRange(result.currentMin, result.currentMax));
      }
    }

    toggleCustomOpenDays(forceCustom) {
      const select = this.controls.open_days_select;
      if (select) {
        const optionValues = Array.from(select.options).map((option) => option.value);
        if (!optionValues.includes(String(this.state.openDays))) {
          select.value = 'custom';
        }
      }
      const isCustom = forceCustom || select?.value === 'custom';
      const customInput = this.controls.open_days_custom;
      if (!customInput) return;
      customInput.classList.toggle('is-hidden', !isCustom);
      if (!isCustom && this.controls.open_days_select) {
        this.setInputValue(this.controls.open_days_select, String(this.state.openDays));
      }
    }

    toggleActualSpend() {
      const actualInput = this.controls.actual_spend;
      if (!actualInput) return;
      const isActual = this.state.mode === 'actual_spend';
      actualInput.classList.toggle('is-hidden', !isActual);
      if (this.controls.updateResults) {
        const showUpdate = isActual && this.stepState.current >= 4;
        this.controls.updateResults.classList.toggle('is-hidden', !showUpdate);
        if (!showUpdate) {
          this.controls.updateResults.disabled = false;
        }
      }
    }

    updateActualSpendValidation(isInvalid) {
      const requiresManualUpdate = this.state.mode === 'actual_spend' && this.stepState.current >= 4;
      const showInline = requiresManualUpdate && Boolean(isInvalid);
      if (this.outputs.actual_spend_error) {
        this.outputs.actual_spend_error.classList.toggle('is-hidden', !showInline);
      }
      if (this.controls.updateResults) {
        this.controls.updateResults.disabled = requiresManualUpdate ? showInline : false;
      }
    }

    toggleTpControls() {
      if (!this.controls.tp_product) return;
      const disableTp = !this.state.tpEnabled;
      this.controls.tp_product.disabled = disableTp;
      this.controls.tp_product.closest('.tisha-calculator__category')?.classList.toggle('is-disabled', disableTp);
      if (this.controls.tp_preset) this.controls.tp_preset.disabled = disableTp;
      if (this.controls.tp_sheets_min) this.controls.tp_sheets_min.disabled = disableTp;
      if (this.controls.tp_sheets_max) this.controls.tp_sheets_max.disabled = disableTp;
    }

    toggleTowelControls() {
      if (!this.controls.towel_product) return;
      const isAirDryer = this.state.handDryingMode === 'air_dryer';
      const disableTowels = !this.state.towelsEnabled || isAirDryer;
      this.controls.towel_product.disabled = disableTowels;
      this.controls.towel_product.closest('.tisha-calculator__category')?.classList.toggle('is-disabled', disableTowels);
      if (this.controls.towels_enabled) {
        this.controls.towels_enabled.disabled = isAirDryer;
        this.controls.towels_enabled.checked = this.state.towelsEnabled && !isAirDryer;
      }
      if (this.controls.towel_share) this.controls.towel_share.disabled = disableTowels;
      if (this.controls.towel_sheets_min) this.controls.towel_sheets_min.disabled = disableTowels;
      if (this.controls.towel_sheets_max) this.controls.towel_sheets_max.disabled = disableTowels;
      if (this.outputs.towel_excluded_note) {
        this.outputs.towel_excluded_note.classList.toggle('is-hidden', !isAirDryer);
      }
    }

    toggleSeatControls() {
      if (this.controls.seat_share) this.controls.seat_share.disabled = !this.state.seatEnabled;
      if (this.controls.seat_product) {
        this.controls.seat_product.disabled = !this.state.seatEnabled;
        this.controls.seat_product.closest('.tisha-calculator__category')?.classList.toggle(
          'is-disabled',
          !this.state.seatEnabled
        );
      }
    }

    getTishaPrice(product) {
      if (!product || !product.price) return 0;
      if (this.config.settings?.price_display === 'ex_vat') {
        return Math.round(product.price / 1.2);
      }
      return Math.round(product.price);
    }

    getCompetitorPrice(category) {
      const tier = this.state.qualityTier || 'standard';
      let match = this.competitorPricing.find(
        (item) => item.category === category && item.quality_tier === tier
      );
      if (!match && tier === 'value') {
        match = this.competitorPricing.find(
          (item) => item.category === category && (item.quality_tier === 'eco' || item.quality_tier === 'standard')
        );
      }
      if (!match && tier === 'eco') {
        match = this.competitorPricing.find(
          (item) => item.category === category && (item.quality_tier === 'value' || item.quality_tier === 'standard')
        );
      }
      if (!match) {
        return { min: 0, max: 0 };
      }
      return {
        min: Math.round(this.toNumber(match.price_per_case_min, 0) * 100),
        max: Math.round(this.toNumber(match.price_per_case_max, 0) * 100),
      };
    }

    parseBulkConfigValue(value) {
      if (!value) return null;
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch (error) {
          return null;
        }
      }
      if (typeof value === 'object') return value;
      return null;
    }

    normalizeBulkTiers(config) {
      const tiers = Array.isArray(config?.tiers) ? config.tiers : Array.isArray(config) ? config : [];
      return tiers
        .map((tier) => {
          const minQty = this.toNumber(tier?.min_qty ?? tier?.minQty ?? tier?.min, 0);
          const percent = this.toNumber(tier?.percent ?? tier?.percent_off ?? tier?.percentOff, 0);
          return {
            min_qty: minQty,
            percent,
          };
        })
        .filter((tier) => tier.min_qty > 0 && tier.percent > 0)
        .sort((a, b) => a.min_qty - b.min_qty);
    }

    getBulkTier(quantity) {
      let match = null;
      this.bulkTiers.forEach((tier) => {
        if (quantity >= tier.min_qty) match = tier;
      });
      return match;
    }

    isBulkEligible(product) {
      return Boolean(product?.is_case_product && product?.bulk_discount_eligible);
    }

    getSubscriptionPercent(product) {
      if (this.state.purchaseMode !== 'subscribe') return 0;
      if (!product?.subscription_eligible || !product?.selling_plan_id) return 0;
      return this.subscriptionPercent;
    }

    getDiscountInfo(product, minCases, maxCases) {
      const subscriptionPercent = this.getSubscriptionPercent(product);
      if (subscriptionPercent > 0) {
        return {
          type: 'subscription',
          percentMin: subscriptionPercent,
          percentMax: subscriptionPercent,
          tierMin: null,
          tierMax: null,
        };
      }

      if (!this.isBulkEligible(product)) {
        return {
          type: null,
          percentMin: 0,
          percentMax: 0,
          tierMin: null,
          tierMax: null,
        };
      }

      const tierMin = this.getBulkTier(minCases);
      const tierMax = this.getBulkTier(maxCases);
      const percentMin = tierMin ? tierMin.percent : 0;
      const percentMax = tierMax ? tierMax.percent : 0;

      return {
        type: percentMin > 0 || percentMax > 0 ? 'bulk' : null,
        percentMin,
        percentMax,
        tierMin,
        tierMax,
      };
    }

    applyDiscount(amount, percent) {
      if (!percent) return amount;
      return Math.round(amount * (1 - percent / 100));
    }

    getCartQuantity(category) {
      const quantityMode = this.config.settings?.cart_quantity_mode || 'median';
      const minCases = category.minCases || 0;
      const maxCases = category.maxCases || 0;
      let quantity = quantityMode === 'median' ? Math.round((minCases + maxCases) / 2) : minCases;
      if (quantityMode === 'max') quantity = maxCases;
      return Math.max(0, quantity);
    }

    getNextBulkTier(quantity) {
      return this.bulkTiers.find((tier) => quantity < tier.min_qty) || null;
    }

    buildBulkUpsell(results) {
      if (!results || !results.categories) return '';
      if (!this.bulkTiers.length) return '';

      let best = null;
      Object.values(results.categories).forEach((category) => {
        if (!category || !category.enabled || !category.product) return;
        if (!this.isBulkEligible(category.product)) return;

        const quantity = this.getCartQuantity(category);
        if (!quantity) return;

        const nextTier = this.getNextBulkTier(quantity);
        if (!nextTier) return;

        const pricePerCase = this.getTishaPrice(category.product);
        if (!pricePerCase) return;

        const annualCases = Math.max(1, quantity) * 12;
        const annualSavings = Math.round(annualCases * pricePerCase * (nextTier.percent / 100));
        if (!annualSavings) return;

        const title =
          category.product?.title_public ||
          category.product?.variant_title ||
          category.product?.key ||
          'this product';

        const candidate = {
          min_qty: nextTier.min_qty,
          percent: nextTier.percent,
          annualSavings,
          title,
        };

        if (!best || candidate.annualSavings > best.annualSavings) {
          best = candidate;
        }
      });

      if (!best) return '';
      const annualLabel = this.currencyFormatter.format(best.annualSavings / 100);
      return `Order ${best.min_qty}+ cases per order to unlock ${best.percent}% bulk pricing. Save about ${annualLabel} per year.`;
    }

    buildDiscountInfo(results) {
      const totals = results?.totals;
      if (!totals) return { tishaNote: '', extraNote: '' };

      const discountMin = totals.discountMin || 0;
      const discountMax = totals.discountMax || 0;
      const hasDiscount = discountMax > 0;

      let tishaNote = '';
      let extraNote = '';

      if (this.state.purchaseMode === 'subscribe') {
        if (hasDiscount) {
          const range = this.formatMoneyRange(discountMin, discountMax);
          tishaNote = `Includes subscription savings: ${range} per month`;
        } else if (this.subscriptionPercent > 0) {
          extraNote = 'Subscription discount is not available for the selected products.';
        }
        return { tishaNote, extraNote };
      }

      if (hasDiscount) {
        const range = this.formatMoneyRange(discountMin, discountMax);
        tishaNote = `Bulk discount applied: ${range} per month`;
        return { tishaNote, extraNote };
      }

      extraNote = this.buildBulkUpsell(results);
      return { tishaNote, extraNote };
    }

    addKitToCart() {
      if (!this.lastResults) this.update(true);
      this.trackCalcComplete(this.lastResults);
      const kitId = this.getKitId(this.lastResults);
      const kitValueCents = this.getKitValue(this.lastResults);
      this.track('get_kit_click', this.lastResults, {
        kit_id: kitId,
        kit_value: this.toCurrencyValue(kitValueCents),
      });

      const items = this.buildCartItems();
      if (!items.length) return;
      const addToCartPayload = this.buildAddToCartPayload(this.lastResults);

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        formData.append(`items[${index}][properties][_tisha_calc]`, '1');
        if (kitId) {
          formData.append(`items[${index}][properties][_tisha_calc_kit]`, kitId);
        }
        if (item.selling_plan) {
          formData.append(`items[${index}][selling_plan]`, item.selling_plan);
        }
      });

      if (cart && cart.getSectionsToRender) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(document.activeElement);
      }

      config.body = formData;

      const cartAddUrl = window?.routes?.cart_add_url || '/cart/add';
      const cartUrl = window?.routes?.cart_url || '/cart';

      fetch(cartAddUrl, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.showError(response.description || 'Unable to add items to cart.');
            return;
          }
          if (addToCartPayload) {
            this.track('kit_add_to_cart', this.lastResults, addToCartPayload);
          }
          if (cart && cart.renderContents) {
            cart.renderContents(response);
          } else {
            window.location = cartUrl;
          }
        })
        .catch(() => {
          window.location = cartUrl;
        });
    }

    getCategoriesInKit(results = this.lastResults) {
      const categories = [];
      if (!results || !results.categories) return categories;

      if (results.categories.toilet_paper?.enabled && results.categories.toilet_paper.product) {
        categories.push('toilet_paper');
      }
      if (results.categories.towels?.enabled && results.categories.towels.product) {
        categories.push('towels');
      }
      if (results.categories.seat_covers?.enabled && results.categories.seat_covers.product) {
        categories.push('seat_covers');
      }

      return categories;
    }


    buildCartItems() {
      const items = [];

      const addItem = (category) => {
        if (!category.enabled || !category.product) return;
        const quantity = this.getCartQuantity(category);
        if (quantity < 1) return;
        const item = { id: category.product.variant_id, quantity };
        if (
          this.state.purchaseMode === 'subscribe' &&
          category.product.subscription_eligible &&
          category.product.selling_plan_id
        ) {
          item.selling_plan = category.product.selling_plan_id;
        }
        items.push(item);
      };

      addItem(this.lastResults.categories.toilet_paper);
      addItem(this.lastResults.categories.towels);
      addItem(this.lastResults.categories.seat_covers);

      return items;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
    }

    setOutput(key, value) {
      if (!this.outputs[key]) return;
      this.outputs[key].textContent = value;
    }

    setInputValue(input, value) {
      if (!input) return;
      if (Array.isArray(input)) {
        input.forEach((node) => {
          node.checked = node.value === value;
        });
        return;
      }
      input.value = value;
    }

    setInputChecked(input, checked) {
      if (!input) return;
      input.checked = Boolean(checked);
    }

    setRadioValue(radios, value) {
      if (!radios) return;
      radios.forEach((radio) => {
        radio.checked = radio.value === value;
      });
    }

    toNumber(value, fallback = 0) {
      const number = Number(value);
      return Number.isFinite(number) ? number : fallback;
    }

    normalizeShare(value, fallback) {
      const number = this.toNumber(value, fallback);
      if (number > 1) return number / 100;
      return number;
    }

    normalizeBoolean(value) {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return normalized === 'true' || normalized === '1';
      }
      if (typeof value === 'number') return value === 1;
      return false;
    }

    formatMoneyRange(minValue, maxValue) {
      const min = Math.min(minValue, maxValue);
      const max = Math.max(minValue, maxValue);
      if (min === 0 && max === 0) return '-';
      if (Math.round(min) === Math.round(max)) {
        return this.currencyFormatter.format(min / 100);
      }
      return `${this.currencyFormatter.format(min / 100)}–${this.currencyFormatter.format(max / 100)}`;
    }

    formatPercentRange(min, max) {
      if (!Number.isFinite(min) || !Number.isFinite(max)) return '-';
      const minPercent = Math.round(min * 100);
      const maxPercent = Math.round(max * 100);
      if (!Number.isFinite(minPercent) || !Number.isFinite(maxPercent)) return '-';
      if (minPercent === maxPercent) return `${minPercent}%`;
      return `${minPercent}%–${maxPercent}%`;
    }

    formatPercent(value) {
      if (!Number.isFinite(value)) return '-';
      return `${Math.round(value * 100)}%`;
    }

    formatPenceRange(minValue, maxValue) {
      const min = Math.min(minValue, maxValue);
      const max = Math.max(minValue, maxValue);
      const format = (value) => `${value.toFixed(1)}p`;
      if (min === 0 && max === 0) return '-';
      if (Math.abs(min - max) < 0.05) return format(min);
      return `${format(min)}–${format(max)}`;
    }

    formatNumberRange(minValue, maxValue) {
      const min = Math.round(Math.min(minValue, maxValue));
      const max = Math.round(Math.max(minValue, maxValue));
      if (!Number.isFinite(min) || !Number.isFinite(max)) return '-';
      if (min === max) return this.integerFormatter.format(min);
      return `${this.integerFormatter.format(min)}–${this.integerFormatter.format(max)}`;
    }

    tpPresetValues(preset) {
      const map = {
        standard: { min: 8, max: 12 },
        controlled: { min: 5, max: 8 },
        premium: { min: 10, max: 14 },
        custom: { min: this.state.tpSheetsMin, max: this.state.tpSheetsMax },
      };
      return map[preset] || map.standard;
    }

    resolveTpPreset(minValue, maxValue) {
      const presets = [
        { key: 'standard', min: 8, max: 12 },
        { key: 'controlled', min: 5, max: 8 },
        { key: 'premium', min: 10, max: 14 },
      ];
      const match = presets.find((preset) => preset.min === minValue && preset.max === maxValue);
      return match ? match.key : 'custom';
    }

    buildBasePayload() {
      return {
        page_type: this.config.context?.page_type,
        product_handle: this.config.context?.product_handle,
        section_id: this.sectionId,
        profile_key: this.state.profileKey || null,
        location_type: this.state.locationType || null,
        visits_per_day: this.state.visitsPerDay,
        open_days_per_month: this.state.openDays,
        tp_product_key: this.state.tpProductKey,
        towel_product_key: this.state.towelProductKey,
        seat_cover_product_key: this.state.seatProductKey,
        hand_drying_mode: this.state.handDryingMode,
        purchase_mode: this.state.purchaseMode,
      };
    }

    track(name, results, extra = {}) {
      const payload = this.buildBasePayload();
      payload.event = name;

      Object.assign(payload, extra);

      if (results?.totals) {
        payload.tisha_cost_min = results.totals.tishaMin;
        payload.tisha_cost_max = results.totals.tishaMax;
        payload.current_cost_min = results.totals.currentMin;
        payload.current_cost_max = results.totals.currentMax;
        payload.saving_min = results.totals.savingMin;
        payload.saving_max = results.totals.savingMax;
        payload.saving_percent_min = results.totals.savingPercentMin;
        payload.saving_percent_max = results.totals.savingPercentMax;
      }

      if (!window.dataLayer) {
        window.dataLayer = [];
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(payload);
      }

      document.dispatchEvent(
        new CustomEvent('tisha:calculator', {
          detail: payload,
        })
      );
    }
  }

  const initCalculators = (scope = document) => {
    scope.querySelectorAll('[data-tisha-calculator]').forEach((root) => {
      new TishaCalculator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCalculators());
  } else {
    initCalculators();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCalculators(event.target);
  });
})();
